import { createPortal } from 'react-dom';
import { motion, AnimatePresence } from 'framer-motion';

interface TourSpotlightProps {
  targetRect: DOMRect | null;
  padding?: number;
  isActive: boolean;
  onOverlayClick: () => void;
}

const springTransition = {
  type: 'spring' as const,
  stiffness: 300,
  damping: 30,
};

export function TourSpotlight({
  targetRect,
  padding = 8,
  isActive,
  onOverlayClick,
}: TourSpotlightProps) {
  if (!isActive) return null;

  const content = (
    <AnimatePresence>
      {isActive && (
        <motion.div
          className="fixed inset-0 z-overlay"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          transition={{ duration: 0.2 }}
        >
          {/* Dark overlay with cutout */}
          {targetRect ? (
            <motion.div
              className="absolute inset-0 bg-black/50 cursor-pointer"
              onClick={onOverlayClick}
              animate={{
                clipPath: `polygon(
                  0% 0%,
                  0% 100%,
                  ${targetRect.left - padding}px 100%,
                  ${targetRect.left - padding}px ${targetRect.top - padding}px,
                  ${targetRect.left + targetRect.width + padding}px ${targetRect.top - padding}px,
                  ${targetRect.left + targetRect.width + padding}px ${targetRect.top + targetRect.height + padding}px,
                  ${targetRect.left - padding}px ${targetRect.top + targetRect.height + padding}px,
                  ${targetRect.left - padding}px 100%,
                  100% 100%,
                  100% 0%
                )`,
              }}
              transition={springTransition}
            />
          ) : (
            <div
              className="absolute inset-0 bg-black/50 cursor-pointer"
              onClick={onOverlayClick}
            />
          )}

          {/* Highlight ring around target */}
          {targetRect && (
            <motion.div
              className="absolute border-2 border-primary rounded-lg shadow-lg shadow-primary/20 pointer-events-none"
              animate={{
                top: targetRect.top - padding,
                left: targetRect.left - padding,
                width: targetRect.width + padding * 2,
                height: targetRect.height + padding * 2,
              }}
              transition={springTransition}
            />
          )}
        </motion.div>
      )}
    </AnimatePresence>
  );

  return createPortal(content, document.body);
}
